import React from 'react'
import { useIntl } from 'react-intl'

import { BackToPiloteButton } from '@/components/atoms/BackToPiloteButton/BackToPiloteButton.tsx'
import { Button } from '@/components/atoms/Button/Button.tsx'
import { NotificationSound } from '@/components/molecules/NotificationSound/NotificationSound.tsx'
import { SupportHeaderSlot } from '@/components/organisms/SupportButton/SupportHeaderSlot.tsx'
import { cn } from '@/lib/utils.ts'

export interface AdminHeaderProps extends React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>> {
  title?: string
  titleClassName?: string
  regionName?: string
  onBackToPiloteClicked?: () => void | Promise<void>
  backToPilote?: 'arrow' | 'button'
  notificationSoundState?: 'enabled' | 'muted'
  onNotificationSoundToggled?: () => void
}

export const AdminHeader = React.forwardRef<HTMLDivElement, AdminHeaderProps>(
  (
    {
      className,
      title,
      titleClassName,
      regionName,
      onBackToPiloteClicked,
      backToPilote = 'arrow',
      notificationSoundState,
      onNotificationSoundToggled,
      children,
      ...props
    },
    ref
  ) => {
    const intl = useIntl()
    const notificationSoundLabel = intl.formatMessage({
      id: notificationSoundState === 'enabled' ? 'notificationSound.operations.on' : 'notificationSound.operations.off',
    })
    return (
      <div
        className={cn(
          'sticky top-0 z-10 flex h-[49px] w-full items-center justify-between bg-gray-800 pr-16 pl-10',
          className
        )}
        ref={ref}
        {...props}>
        <p className={cn('text-xl select-none', titleClassName || 'text-orange-300')}>
          {title ?? intl.formatMessage({ id: 'app.name' })}
        </p>

        <div className="flex items-center gap-4">
          {children}
          <p className="text-base font-bold text-white select-none">{regionName}</p>
          {notificationSoundState && onNotificationSoundToggled && (
            <NotificationSound
              state={notificationSoundState}
              aria-pressed={notificationSoundState === 'enabled'}
              aria-label={notificationSoundLabel}
              title={notificationSoundLabel}
              onClick={onNotificationSoundToggled}
            />
          )}
          <SupportHeaderSlot />
          {onBackToPiloteClicked && backToPilote === 'button' && (
            <Button
              data-testid="back-to-pilote"
              size="sm"
              className="h-[29px] bg-black px-6 py-1.5 hover:bg-gray-700"
              onClick={onBackToPiloteClicked}>
              {intl.formatMessage({ id: 'message.page.back' })}
            </Button>
          )}
        </div>

        {onBackToPiloteClicked && backToPilote === 'arrow' && (
          <div className="absolute top-12 left-0 flex w-[39px] shrink-0 flex-col items-start justify-center bg-gray-800">
            <BackToPiloteButton onClick={onBackToPiloteClicked} />
          </div>
        )}
      </div>
    )
  }
)
